import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getCampaigns, ApiError } from "../api.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CAMPAIGN_STATUS_META = {
  pending: { label: "Pending", className: "badge-gray" },
  generating: { label: "Generating", className: "badge-gray" },
  dry_run_pending: { label: "Dry-Run Pending", className: "badge-gray" },
  dry_run_complete: { label: "Dry-Run Ready", className: "badge-blue" },
  complete: { label: "Complete", className: "badge-green" },
  live: { label: "Live", className: "badge-green" },
  failed: { label: "Failed", className: "badge-red" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { page, limit };
      if (statusFilter) query.status = statusFilter;
      const res = await getCampaigns(query);
      setCampaigns(res.data || []);
      setTotal(res.total ?? (res.data || []).length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setCampaigns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="campaigns-page container">
      <div className="page-header">
        <h1>Campaigns</h1>
        <div className="page-header-actions">
          <label className="filter-field">
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              {Object.entries(CAMPAIGN_STATUS_META).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="dashboard-table-wrap">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Campaign ID</th>
              <th>IO #</th>
              <th>Platform</th>
              <th>Status</th>
              <th>Dry Run</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="table-loading">Loading...</td></tr>
            )}
            {!loading && campaigns.length === 0 && (
              <tr><td colSpan={7} className="table-empty">
                No campaigns yet. Approve an IO and trigger generation from the Campaign Preview page.
              </td></tr>
            )}
            {!loading && campaigns.map((c) => {
              const meta = CAMPAIGN_STATUS_META[c.status] || { label: c.status, className: "badge-gray" };
              return (
                <tr key={c.id} className="dashboard-row">
                  <td className="mono">{c.id?.slice(0, 8)}...</td>
                  <td>
                    {c.ioId ? (
                      <Link to={`/ios/${encodeURIComponent(c.ioId)}`} className="cell-link">
                        {c.ioId}
                      </Link>
                    ) : "—"}
                  </td>
                  <td>{c.platform || "—"}</td>
                  <td>
                    <span className={`status-badge ${meta.className}`}>{meta.label}</span>
                  </td>
                  <td>{c.dryRun ? "Yes" : "No"}</td>
                  <td className="muted">{formatDateTime(c.createdAt)}</td>
                  <td className="muted">{formatDateTime(c.updatedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dashboard-pagination">
        <span className="pagination-info">
          Page {page} of {totalPages} - {total} campaigns
        </span>
        <div className="pagination-controls">
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage(1)}>
            {"<<"}
          </button>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            {"<"}
          </button>
          <span className="pagination-current">{page}</span>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            {">"}
          </button>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
}